import { useState, useContext } from 'react';
import { AppContext } from '@/App';
import { ClipboardList, Plus, CheckCircle, AlertCircle, Clock, Sun, Moon, TrendingUp, Filter } from 'lucide-react';

function DailyProcessingPlan() {
  const { API } = useContext(AppContext);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedShift, setSelectedShift] = useState('Day');

  // Mock data for processing plan
  const processingPlan = [
    { id: 'PL-001', machine: 'Shredder 1', scrapType: 'Taldon', plannedQty: 8000, actualQty: 7500, status: 'In Progress', shift: 'Day', startTime: '06:00', endTime: '14:00' },
    { id: 'PL-002', machine: 'Shredder 2', scrapType: 'Telic', plannedQty: 8000, actualQty: 8200, status: 'Completed', shift: 'Day', startTime: '06:00', endTime: '13:30' },
    { id: 'PL-003', machine: 'Breaker 1', scrapType: 'Twitch', plannedQty: 4000, actualQty: 3800, status: 'In Progress', shift: 'Day', startTime: '06:00', endTime: '14:00' },
    { id: 'PL-004', machine: 'Baler 1', scrapType: 'Talc', plannedQty: 2000, actualQty: 2100, status: 'Completed', shift: 'Day', startTime: '06:00', endTime: '12:00' },
    { id: 'PL-005', machine: 'Sorting Line 1', scrapType: 'Mixed Scrap', plannedQty: 12000, actualQty: 10500, status: 'In Progress', shift: 'Day', startTime: '06:00', endTime: '18:00' },
    { id: 'PL-006', machine: 'Shredder 1', scrapType: 'Tally', plannedQty: 8000, actualQty: 0, status: 'Planned', shift: 'Night', startTime: '18:00', endTime: '02:00' },
    { id: 'PL-007', machine: 'Shredder 2', scrapType: 'Taint Tabor', plannedQty: 8000, actualQty: 0, status: 'Planned', shift: 'Night', startTime: '18:00', endTime: '02:00' },
    { id: 'PL-008', machine: 'Breaker 1', scrapType: 'Taldon', plannedQty: 4000, actualQty: 0, status: 'Planned', shift: 'Night', startTime: '18:00', endTime: '02:00' },
  ];

  const filteredPlan = processingPlan.filter(p => p.shift === selectedShift);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Planned': return 'bg-gray-100 text-gray-700';
      case 'Delayed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed': return <CheckCircle size={16} />;
      case 'In Progress': return <Clock size={16} />;
      case 'Planned': return <Clock size={16} />;
      case 'Delayed': return <AlertCircle size={16} />;
      default: return null;
    }
  };

  const totalPlanned = filteredPlan.reduce((sum, p) => sum + p.plannedQty, 0);
  const totalActual = filteredPlan.reduce((sum, p) => sum + p.actualQty, 0);
  const completedTasks = filteredPlan.filter(p => p.status === 'Completed').length;

  return (
    <div className="space-y-6" data-testid="daily-processing-plan-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="text-[#1a237e]" size={28} />
            Daily Processing Plan
          </h1>
          <p className="text-gray-600 mt-1">Plan and track daily material processing activities</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] transition">
          <Plus size={18} />
          Create Plan
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-gray-400" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] outline-none"
          />
        </div>
        <div className="flex bg-gray-100 rounded-lg p-1">
          {['Day', 'Night'].map((shift) => (
            <button
              key={shift}
              onClick={() => setSelectedShift(shift)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1 ${
                selectedShift === shift
                  ? 'bg-white text-[#1a237e] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {shift === 'Day' ? <Sun size={14} /> : <Moon size={14} />}
              {shift} Shift
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#1a237e] to-[#283593] rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Planned Tasks</p>
          <p className="text-3xl font-bold mt-1">{filteredPlan.length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Completed</p>
          <p className="text-3xl font-bold mt-1">{completedTasks}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Planned Qty</p>
          <p className="text-3xl font-bold mt-1">{(totalPlanned / 1000).toFixed(1)} T</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 text-white">
          <div className="flex items-center gap-1">
            <p className="text-sm opacity-80">Actual Qty</p>
            <TrendingUp size={14} className="opacity-80" />
          </div>
          <p className="text-3xl font-bold mt-1">{(totalActual / 1000).toFixed(1)} T</p>
          <p className="text-xs opacity-70">{totalPlanned > 0 ? ((totalActual / totalPlanned) * 100).toFixed(0) : 0}% achieved</p>
        </div>
      </div>

      {/* Shift Header */}
      <div className={`rounded-xl p-4 ${
        selectedShift === 'Day' 
          ? 'bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200' 
          : 'bg-gradient-to-r from-indigo-100 to-purple-100 border border-indigo-200'
      }`}>
        <div className="flex items-center gap-2">
          {selectedShift === 'Day' ? <Sun className="text-amber-600" size={24} /> : <Moon className="text-indigo-600" size={24} />}
          <div>
            <h3 className="font-bold text-gray-900">{selectedShift} Shift Processing Plan</h3>
            <p className="text-sm text-gray-600">
              {selectedShift === 'Day' ? '06:00 AM - 06:00 PM' : '06:00 PM - 06:00 AM'}
            </p>
          </div>
        </div>
      </div>

      {/* Plan Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Plan ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Machine</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Scrap Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Time</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Planned (kg)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actual (kg)</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Variance</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPlan.map((plan) => {
                const variance = plan.actualQty - plan.plannedQty;
                const variancePercent = plan.plannedQty > 0 ? ((variance / plan.plannedQty) * 100).toFixed(1) : 0;
                
                return (
                  <tr key={plan.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{plan.id}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{plan.machine}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                        {plan.scrapType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                      {plan.startTime} - {plan.endTime}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                      {plan.plannedQty.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-blue-600">
                      {plan.actualQty.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {plan.actualQty > 0 && (
                        <span className={`text-xs font-medium ${
                          variance >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {variance >= 0 ? '+' : ''}{variancePercent}%
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(plan.status)}`}>
                        {getStatusIcon(plan.status)}
                        {plan.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DailyProcessingPlan;
